import { useEffect, useRef } from 'react';
import { addAfterEffect, useFrame, useThree } from '@react-three/fiber';
import { CALLBACK_ORDER_FRAME_TIMINGS } from './FrameCallbackOrder';

export type FrameTimings = {
  // measures period between start and finish frame rendering (time of renderer.render(...) call)
  avgFrameRenderingTime: number;
  lastFrameRenderingTime: number;
  frameStartTime: number;
  // measures period between even frame start and odd frame start,
  // so it includes everything and it's more sensetive to any load
  isEvenFrame: boolean;
  avgFrameRenderingTime2: number;
  lastFrameRenderingTime2: number;
  frameStartTime2: number;
  avgFrameRenderingTime3: number;
  spikeFilterBudget: number;
  frameCount: number;
  // frameCountMod10: number;
  // frameTimes: number[];
};

const AVG_FRAME_TIME_SMOOTHING_K0 = 1 / 50;
const AVG_FRAME_TIME2_SMOOTHING_K0 = 1 / 50;

const INITIAL_FRAME_TIME = 30;

const SPIKE_FILTER_LIMIT = 3;
const NORMAL_FRAMES_PER_SPIKE = 5;
const SPIKE_FRAMES_AT_ONCE = 2;

export const useFrameTimings = () => {
  const invalidate = useThree((state) => state.invalidate);

  const frameTimings = useRef<FrameTimings>({
    // time between start and finish of one frame rendering
    avgFrameRenderingTime: INITIAL_FRAME_TIME,
    lastFrameRenderingTime: INITIAL_FRAME_TIME,
    frameStartTime: 0,
    isEvenFrame: false,
    // time between even frame start and odd frame start
    avgFrameRenderingTime2: INITIAL_FRAME_TIME,
    lastFrameRenderingTime2: INITIAL_FRAME_TIME,
    frameStartTime2: 0,
    // filtered frameTime2 without high spikes
    avgFrameRenderingTime3: INITIAL_FRAME_TIME,
    spikeFilterBudget: 0,
    frameCount: 0,
    // frameCountMod10: 0,
    // frameTimes: [],
  });

  useFrame(() => {
    const { current } = frameTimings;
    current.frameStartTime = performance.now();
    current.isEvenFrame = !current.isEvenFrame;

    current.frameCount += 1;

    if (current.isEvenFrame) {
      current.frameStartTime2 = current.frameStartTime;
      // make sure we start rendering odd frame right after this frame
      invalidate();
    } else {
      current.lastFrameRenderingTime2 = current.frameStartTime - current.frameStartTime2;
      const k0 = AVG_FRAME_TIME2_SMOOTHING_K0;
      current.avgFrameRenderingTime2 =
        current.lastFrameRenderingTime2 * k0 + current.avgFrameRenderingTime2 * (1 - k0);

      if (
        current.lastFrameRenderingTime2 > SPIKE_FILTER_LIMIT * current.avgFrameRenderingTime3 &&
        current.spikeFilterBudget >= NORMAL_FRAMES_PER_SPIKE
      ) {
        current.spikeFilterBudget -= NORMAL_FRAMES_PER_SPIKE;
      } else {
        current.avgFrameRenderingTime3 =
          current.lastFrameRenderingTime2 * k0 + current.avgFrameRenderingTime3 * (1 - k0);

        if (current.spikeFilterBudget < SPIKE_FRAMES_AT_ONCE * NORMAL_FRAMES_PER_SPIKE) {
          current.spikeFilterBudget += 1;
        }
      }
      // current.frameTimes.push(Math.trunc(current.lastFrameRenderingTime2));
    }
    // ensure it starts first to save start time
  }, CALLBACK_ORDER_FRAME_TIMINGS);

  useEffect(() => {
    return addAfterEffect(() => {
      const { current } = frameTimings;
      current.lastFrameRenderingTime = performance.now() - current.frameStartTime;
      const k0 = AVG_FRAME_TIME_SMOOTHING_K0;

      current.avgFrameRenderingTime =
        current.lastFrameRenderingTime * k0 + current.avgFrameRenderingTime * (1 - k0);

      // current.frameCountMod10 += 1;
      // if (current.frameCountMod10 >= 10) {
      //   current.frameCountMod10 = 0;
      //   const frameTimesString = current.frameTimes.toLocaleString();
      //   current.frameTimes = [];
      //   console.log(
      //     'fc',
      //     current.frameCount,
      //     'avg',
      //     Math.trunc(current.avgFrameRenderingTime),
      //     'avg2',
      //     Math.trunc(current.avgFrameRenderingTime2),
      //     'avg3',
      //     Math.trunc(current.avgFrameRenderingTime3)
      //     'frameTimes',
      //     frameTimesString
      //   );
      // }
    });
  }, []);

  return frameTimings;
};
